import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion';
interface Tilt3DProps {
  children: React.ReactNode;
  className?: string;
  /** Maximum rotation in degrees. Keep it small — this is depth, not a gimmick. */
  max?: number;
  /** Lifts the card toward the viewer while the pointer is over it. */
  lift?: number;
}

/**
 * Pointer-driven 3D tilt for cards and panels.
 *
 * Only responds to a fine pointer (mouse / trackpad): on touch it stays perfectly
 * flat, so nothing shifts under a thumb while scrolling. Fully disabled under
 * prefers-reduced-motion.
 */
export function Tilt3D({
  children,
  className = '',
  max = 5,
  lift = 6
}: Tilt3DProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const z = useMotionValue(0);
  const spring = {
    stiffness: 170,
    damping: 20,
    mass: 0.4
  };
  const rotateX = useSpring(useTransform(py, [-0.5, 0.5], [max, -max]), spring);
  const rotateY = useSpring(useTransform(px, [-0.5, 0.5], [-max, max]), spring);
  const translateZ = useSpring(z, spring);
  if (reduce) return <div className={className} data-id="element-420" data-mp="11nb09vh7gauz">{children}</div>;
  const handleMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse') return;
    const node = ref.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    px.set((event.clientX - rect.left) / rect.width - 0.5);
    py.set((event.clientY - rect.top) / rect.height - 0.5);
    z.set(lift);
  };
  const handleLeave = () => {
    px.set(0);
    py.set(0);
    z.set(0);
  };
  return <div ref={ref} className={className} style={{
    perspective: 1000
  }} data-id="element-421" data-mp="15mtgwf1ztzs0">
      <motion.div onPointerMove={handleMove} onPointerLeave={handleLeave} style={{
      rotateX,
      rotateY,
      translateZ,
      transformStyle: 'preserve-3d'
    }} className="h-full will-change-transform" data-id="element-422" data-mp="1akv2b6mw6b6n">
        {children}
      </motion.div>
    </div>;
}